import React, { FC } from 'react';
import { SxProps } from '@mui/system';
import capitalize from 'lodash/capitalize';
import { useIsMobile, useTheme } from '@cp/ds/src/hooks';
import { MoreProps } from '@cp/ds/src/components/tagCloud/types';
import { Link } from '@cp/ds/src/components/link';
import { Box } from '@cp/ds/src/components/box';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { StyledUnorderedList } from '@cp/ds/src/components/styled/styledUnorderedList';
import { StyledListItem } from '@cp/ds/src/components/styled/styledListItem';
import { Tag } from '@cp/ds/src/components/tag';
import { TagCloudProps } from './types';

export const TagCloud: React.FC<TagCloudProps> = ({ items, sx, size = 'normal', unfilled }) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [isExpanded, setExpanded] = React.useState(false);

  const MAX_VISIBLE_ITEMS_BY_DEFAULT = isMobile ? 3 : 5;

  const style: Record<'mx' | 'my' | 'negative', Record<typeof size, number>> = {
    mx: {
      normal: 0.75,
      small: 0.25,
    },
    my: {
      normal: 1,
      small: 0.25,
    },
    negative: {
      normal: -0.75,
      small: -0.25,
    },
  };

  const filledSx: SxProps = !unfilled
    ? {
        p: 3.75,
        border: `1px solid ${theme.palette['brand gray']}`,
        borderRadius: `${theme.borderRadius.extraLarge}px`,
        backgroundColor: 'white',
      }
    : {
        width: isExpanded ? '100%' : { xs: '90%', md: '58%' },
      };
  const handleOpen = () => setExpanded(true);
  const More: FC<MoreProps> = ({ onOpen }) => (
    <Link disableHover onClick={onOpen} sx={{ mt: 0.75, mx: 0.75, fontSize: '12px' }}>
      ещё
    </Link>
  );

  return (
    <Box sx={mergeSx(filledSx, sx)}>
      <StyledUnorderedList sx={{ mx: style.negative[size], display: 'flex', flexWrap: 'wrap' }}>
        {items.map(({ key, label, ...restItemProps }, index) => (
          <StyledListItem
            key={key}
            sx={{
              my: style.my[size],
              mx: style.mx[size],
              display: index >= MAX_VISIBLE_ITEMS_BY_DEFAULT && !isExpanded ? 'none' : undefined,
            }}
          >
            <Tag
              href={'href' in restItemProps ? restItemProps.href : undefined}
              onClick={'onClick' in restItemProps ? restItemProps.onClick : undefined}
              sx={{ fontSize: '12px' }}
            >
              {typeof label === 'string' ? capitalize(label) : label}
            </Tag>
          </StyledListItem>
        ))}
        {!isExpanded && items.length > MAX_VISIBLE_ITEMS_BY_DEFAULT && <More onOpen={handleOpen} />}
      </StyledUnorderedList>
    </Box>
  );
};
